import type { FilterDef, StatusTone } from '@org/ui';
import { USER_TYPES } from '@org/model';

/**
 * Pure rules behind the students list toolbar: which segment a student falls
 * in, what the "Situação" chip says, and who is "waiting on an admin".
 */

export type StudentRowLike = {
  userId: string;
  type?: string | null;
  status?: string | null;
  enabled?: boolean | null;
  identityStatus?: string | null;
  signupSource?: string | null;
  countryName?: string | null;
  levelId?: string | null;
};

export const STUDENT_SEGMENTS = ['all', 'group', 'private', 'self-learning', 'b2b', 'pending'] as const;
export type StudentSegment = (typeof STUDENT_SEGMENTS)[number];

/** Self sign-ups whose photo a human still has to decide on. */
export function needsIdentityDecision(student: StudentRowLike): boolean {
  return (
    student.signupSource === 'self' &&
    (student.identityStatus === 'needs_review' || student.identityStatus === 'declined')
  );
}

/** Invited-type students who were never sent their invite. */
export function inviteNotSent(student: StudentRowLike): boolean {
  return student.signupSource !== 'self' && student.status === 'new' && student.enabled !== false;
}

/**
 * "Pendentes": anything an admin must act on — an invite to send, a photo
 * to review, or (when the ids are known) an open access request.
 */
export function isPendingOnAdmin(student: StudentRowLike, pendingAccessUserIds: ReadonlySet<string> = new Set()): boolean {
  return needsIdentityDecision(student) || inviteNotSent(student) || pendingAccessUserIds.has(student.userId);
}

export function matchesSegment(
  student: StudentRowLike,
  segment: StudentSegment,
  pendingAccessUserIds: ReadonlySet<string> = new Set(),
): boolean {
  if (segment === 'all') return true;
  if (segment === 'pending') return isPendingOnAdmin(student, pendingAccessUserIds);
  return student.type === segment;
}

export function countBySegment(
  students: StudentRowLike[],
  pendingAccessUserIds: ReadonlySet<string> = new Set(),
): Record<StudentSegment, number> {
  const counts = { all: 0, group: 0, private: 0, 'self-learning': 0, b2b: 0, pending: 0 } as Record<StudentSegment, number>;
  for (const s of students) {
    for (const seg of STUDENT_SEGMENTS) if (matchesSegment(s, seg, pendingAccessUserIds)) counts[seg] += 1;
  }
  return counts;
}

export type StudentSituation =
  | 'disabled'
  | 'identityPending'
  | 'identityDeclined'
  | 'identityUnverified'
  | 'notInvited'
  | 'invited'
  | 'awaitingTerms'
  | 'joined'
  | 'unknown';

/**
 * The one state that matters right now. Disabled wins; then a self sign-up's
 * validation; then the invite/terms/joined ladder.
 * TODO(base access): the list payload has no trial/subscription window, so
 * "Teste grátis · N dias" / "Assinatura ativa" / "Expirado" are not shown
 * here yet — they live in the student's Access dialog.
 */
export function situationOf(student: StudentRowLike): StudentSituation {
  if (student.enabled === false || student.status === 'disabled') return 'disabled';
  if (student.signupSource === 'self') {
    if (student.identityStatus === 'needs_review') return 'identityPending';
    if (student.identityStatus === 'declined') return 'identityDeclined';
    if (student.identityStatus === 'unverified') return 'identityUnverified';
  }
  switch (student.status) {
    case 'new':
      return student.signupSource === 'self' ? 'awaitingTerms' : 'notInvited';
    case 'invited':
      return 'invited';
    case 'awaiting-terms':
      return 'awaitingTerms';
    case 'joined':
      return 'joined';
    default:
      return 'unknown';
  }
}

export const situationTone: Record<StudentSituation, StatusTone> = {
  disabled: 'neutral',
  identityPending: 'warning',
  identityDeclined: 'danger',
  identityUnverified: 'info',
  notInvited: 'warning',
  invited: 'info',
  awaitingTerms: 'info',
  joined: 'success',
  unknown: 'neutral',
};

/** Invite is possible only for admin-created students who have not joined. */
export function canInvite(student: StudentRowLike): boolean {
  return student.signupSource !== 'self' && student.status === 'new';
}

export const STUDENT_SITUATIONS: readonly StudentSituation[] = [
  'joined',
  'invited',
  'awaitingTerms',
  'notInvited',
  'identityUnverified',
  'identityPending',
  'identityDeclined',
  'disabled',
  'unknown',
];

export type StudentFilterLabels = {
  country: string;
  allCountries: string;
  level: string;
  allLevels: string;
  type: string;
  allTypes: string;
  situation: string;
  allSituations: string;
  enabledOnly: string;
  /** Display name per student type, in any order. */
  typeNames: Record<(typeof USER_TYPES)[number], string>;
  /** Display name per situação chip. */
  situationNames: Record<StudentSituation, string>;
};

/** How many students carry each value of a field (missing values are skipped). */
export function countBy<T>(rows: readonly T[], pick: (row: T) => string | null | undefined): Map<string, number> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const key = pick(row);
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

/**
 * The Filters panel sections for the students page, in the fixed order of the
 * old toolbar (left → right): País, Nível, Tipo, Situação, then the "só
 * ativas" switch. Every option carries how many of the loaded students have
 * that value, and only values present among them are offered (a level or
 * type nobody has is left out). Countries alphabetical (pt-BR); levels
 * exactly as the API returns them; types in USER_TYPES order; situações in
 * STUDENT_SITUATIONS order.
 */
export function buildStudentFilterDefs(
  labels: StudentFilterLabels,
  students: readonly StudentRowLike[],
  levels: readonly { levelId: string; name: string }[],
): FilterDef[] {
  const collator = new Intl.Collator('pt-BR', { sensitivity: 'base' });
  const byCountry = countBy(students, (s) => s.countryName);
  const byLevel = countBy(students, (s) => s.levelId);
  const byType = countBy(students, (s) => s.type);
  const bySituation = countBy(students, (s) => situationOf(s));
  return [
    {
      key: 'country',
      label: labels.country,
      type: 'select',
      allLabel: labels.allCountries,
      options: [...byCountry.keys()].sort(collator.compare).map((c) => ({ value: c, label: c, count: byCountry.get(c) })),
    },
    {
      key: 'level',
      label: labels.level,
      type: 'select',
      allLabel: labels.allLevels,
      options: levels
        .filter((l) => byLevel.has(l.levelId))
        .map((l) => ({ value: l.levelId, label: l.name, count: byLevel.get(l.levelId) })),
    },
    {
      key: 'type',
      label: labels.type,
      type: 'select',
      allLabel: labels.allTypes,
      options: USER_TYPES.filter((v) => byType.has(v)).map((value) => ({ value, label: labels.typeNames[value], count: byType.get(value) })),
    },
    {
      key: 'situation',
      label: labels.situation,
      type: 'select',
      allLabel: labels.allSituations,
      options: STUDENT_SITUATIONS.filter((v) => bySituation.has(v)).map((value) => ({
        value,
        label: labels.situationNames[value],
        count: bySituation.get(value),
      })),
    },
    { key: 'enabled', label: labels.enabledOnly, type: 'toggle' },
  ];
}
